<template>
    <div
        class="flex flex-col h-[92px] lg:min-h-[92px] lg:h-auto font-oceanside"
    >
        <button
            ref="accordionButton"
            @click="openGroupOverlay(itemData)"
            :class="[
                'border-b border-gray-DFDFDF bg-white box-content pl-[10px] pr-[28px] h-[90px]',
                'lg:min-h-[92px] lg:h-auto flex gap-[10px] items-center hover:bg-gray-F6F6F6',
                'focus:outline-none focus:ring-1 focus:ring-black focus:border-black',
            ]"
        >
            <div class="w-5 shrink-0">
                <!-- {{ itemData.name }}
                {{ hasErrors }}
                {{ hasBeenSeen }}
                {{ selectionWasMade }} -->
                <transition name="cz3__fade" mode="out-in">
                    <!-- Vite doesn't like bundling assets from dynamic attributes thus v-if / v-else -->
                    <img
                        v-if="hasErrors"
                        src="../assets/images/icons/check-red.svg"
                        width="20"
                        height="20"
                        alt=""
                    >
                    <img
                        v-else-if="selectionWasMade && hasBeenSeen"
                        src="../assets/images/icons/check-filled.svg"
                        width="20"
                        height="20"
                        alt=""
                    >
                    <img
                        v-else
                        src="../assets/images/icons/check-grey.svg"
                        width="20"
                        height="20"
                        alt=""
                    >
                </transition>
            </div>

            <div class="py-3 text-left pr-4">
                <div
                    :class="{
                        'text-red-error': hasErrors
                    }"
                    class="text-base font-semibold font-oceanside"
                >
                    <div class="relative">
                        <div class="capitalize">
                            {{ itemDisplayName }}
                        </div>
                        <div
                            v-if="hasErrors"
                            class="flex text-[14px] font-normal text-red-error absolute whitespace-nowrap -mt-[7px] gap-x-[4px]"
                        >
                            {{ $t('PLEASE_MAKE_SELECTIONS') }}
                            <img
                                src="../assets/images/icons/exclamation-red.svg"
                                width="12"
                                height="12"
                                alt="Exclamation mark"
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div class="flex-1" />

            <div class="flex flex-col items-end">
                <current-selection
                    :placement-data="itemData"
                    :class="[
                        'pr-[12px] w-full text-right flex-grow',
                        hasErrors && 'text-red-error',
                    ]"
                    :has-errors="hasErrors"
                />
                <template v-if="!isSurefitHubLocked && (itemData.code === 'Headcover' || itemData.code === 'Grip' || itemData.code === 'PutterShaftBand')">
                    <div
                        class="text-[12px] text-gray-dark text-right w-56 leading-4 pr-[12px] flex"
                        v-if="isLocked"
                    >
                        <crown-icon
                            class="text-gray-dark w-[32px] shrink-0"
                        />
                        <template v-if="itemData.code === 'Headcover'">
                            {{ $t('HEADCOVER_GROUP_LOCKED') }}
                        </template>
                        <template v-else-if="itemData.code === 'Grip'">
                            {{ $t('GRIP_GROUP_LOCKED') }}
                        </template>
                        <template v-else-if="itemData.code === 'PutterShaftBand'">
                            {{ $t('SHAFT_BAND_GROUP_LOCKED') }}
                        </template>
                    </div>
                    <div
                        class="text-[12px] text-right leading-4 pr-[12px] flex"
                        v-else
                    >
                        <crown-icon
                            class="w-[32px] shrink-0"
                        />
                        <span>{{ $t('CUSTOM_OPTIONS_AVAILABLE') }}</span>
                    </div>
                </template>
            </div>

            <img
                v-if="!isSurefitHubLocked"
                src="../assets/images/icons/item-arrow.svg"
                width="9"
                height="16"
                alt="Right arrow"
                :class="{ '-rotate-180': isActive }"
                class="transition-transform duration-300 ease-in-out h-[16px] w-[9.1px]"
            >
        </button>
    </div>
</template>

<script>
    import allStores from '../stores/all';
    import * as constants from '../constants';

    import CurrentSelection from './CurrentSelection.vue';
    import CrownIcon from './CrownIcon.vue';

    export default {
        mixins: [allStores],

        components: {
            CurrentSelection,
            CrownIcon,
        },

        props: {
            itemData: {
                type: Object,
                required: true,
            },
        },

        data() {
            return {
                isActive: false,
                interval: null,
                scrollHeight: 0,
                elementHeight: 0,
                elementHeightChanged: false,
                selectedGroup: null,
                isInitialLoad: true,
            };
        },

        computed: {
            isLocked() {
                return this.customizerStore.isGroupLocked(this.itemData);
            },

            placements() {
                if (!this.itemData) {
                    return [];
                }

                const placements = this.itemData.placements;

                return placements ?? [];
            },

            isSurefitHubLocked() {
                return this.customizerStore.isSurefitHubLocked(this.placements);
            },

            selectionWasMade() {
                if (this.isFaceSoleGroup) {
                    // Check if "No Customization" was explicitly selected
                    const noCustomizationSelected = this.customizerStore.isFrontNoCustomizationSelected('Face/Sole');
                    if (noCustomizationSelected) {
                        return true;
                    }

                    const vc = this.customizerStore.viewController;
                    const initials = vc?.selectedAtPlacement('StampingStyle');
                    const stamps = vc?.selectedAtPlacement('Stamp');

                    const hasStamps = !!(stamps && !stamps.placeholder && stamps.code !== 'Stamp-Stamp-Blank');

                    // Validate initials: selected, required text present, and no profanity errors
                    const initialsSelected = !!(initials && !initials.placeholder);
                    let hasInitials = false;
                    if (initialsSelected) {
                        const requiresText1 = initials?.custom?.['v-stamping-has-text-1'] === 'yes';
                        const text1Value = initials?.custom?.['v-stamping-text-1'];
                        const requiresText2 = initials?.custom?.['v-stamping-has-text-2'] === 'yes';
                        const text2Value = initials?.custom?.['v-stamping-text-2'];

                        const text1Ok = !requiresText1 || (text1Value != null && `${text1Value}`.length > 0);
                        const text2Ok = !requiresText2 || (text2Value != null && `${text2Value}`.length > 0);

                        const panelErrors = this.customizerStore.panelsInErrorState ?? [];
                        const group = 'Face/Sole';
                        const hasProfanityError = panelErrors.includes(`${group}-v-stamping-text-1`) || panelErrors.includes(`${group}-v-stamping-text-2`);

                        hasInitials = text1Ok && text2Ok && !hasProfanityError;
                    }

                    let hasPaintfill = false;

                    if (this.customizerStore.isAnyPaintfillSelected() && this.customizerStore.isPaintfillComplete('Face/Sole')) {
                        hasPaintfill = true;
                    }

                    return hasStamps || hasInitials || hasPaintfill;
                }

                return this.customizerStore.selectionsMade.includes(this.itemData.name);
            },

            isFaceSoleGroup() {
                return this.itemData?.name === 'Face/Sole' || this.itemData?.code === 'Face/Sole';
            },

            paintfillGroupName() {
                if (this.isFaceSoleGroup) {
                    return 'Face/Sole';
                }

                if (this.itemData?.name === 'Top' || this.itemData?.code === 'TopPaintfill') {
                    return 'Top';
                }

                if (this.itemData?.name === 'Back' || this.itemData?.code === 'BackPaintfill') {
                    return 'Back';
                }

                return null;
            },

            hasPaintfillErrors() {
                return !!this.paintfillGroupName
                    && this.customizerStore.isAnyPaintfillSelected()
                    && !this.customizerStore.isPaintfillComplete(this.paintfillGroupName);
            },

            selectionsMade() {
                return this.customizerStore.selectionsMade;
            },

            hasErrors() {
                return (this.hasPaintfillErrors && this.hasBeenSeen)
                    || (this.panelsInErrorState.includes('Main') && !this.selectionWasMade);
            },

            panelsInErrorState() {
                return this.customizerStore.panelsInErrorState;
            },

            isReset() {
                return this.appStore.currentStep === constants.STEP_RESET;
            },

            visibleGroups() {
                return this.customizerStore.visibleGroups;
            },

            currentGroup() {
                return this.customizerStore.currentGroup;
            },

            isCurrentGroup() {
                return this.itemData.name === this.currentGroup;
            },

            showGroupOverlay() {
                return this.customizerStore.showGroupOverlay;
            },

            hasBeenSeen() {
                return this.customizerStore.groupsSeen.includes(this.itemData.name);
            },

            itemDisplayName() {
                return this.itemData.name;
            },
        },

        methods: {
            setSelectedGroup(itemData) {
                this.customizerStore.setCurrentGroup(itemData.name);
            },

            setShowGroupOverlay(value) {
                this.appStore.setShowGroupOverlay(value);
            },

            openGroupOverlay(group) {
                // No overlay.
                if (this.isSurefitHubLocked) {
                    return;
                }

                this.setSelectedGroup(group);
                this.customizerStore.addGroupToSeen(group.name);
                this.setShowGroupOverlay(true);
            },

        },
    };
</script>
